/**
 * @file chargeLines.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-23
 * @modified 2026-10-23
 * @description Is a bill line a CHARGE — haulage, carriage, a surcharge —
 *              rather than goods? One rule, moved here unchanged from
 *              billLineReconcile.isChargeLine (which re-exports it) so the
 *              server can read a bill's haulage the same way Add Bill does
 *              (Pricing & Costing's cost from a bill with a product line and
 *              a haulage line — Cam, 23 Oct 2026).
 *
 *   isChargeLine(text)      by its words alone (Add Bill, bills of jobs)
 *   isChargeBillLine(line)  a stored bill line: on a PO's Direct Delivery
 *                           haulage key (`haulage:<order line>`), an EXTH
 *                           item, a haulage account (320 / 321), or charge
 *                           words
 *
 *   1.1.0 (2026-10-23): a delivery day or slot charged as a line of its own
 *   ("Saturday AM", "Sat delivery", "Bank holiday", "Next day") is a charge
 *   — Burcombe's QA03714 had read "Saturday AM" as goods.
 *
 *   The server's utils/chargeLines.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

/** The purchase accounts a haulage charge is coded to (Bag / Bulk). */
export const HAULAGE_ACCOUNT_CODES = ['320', '321'];

/**
 * Is this bill line a CHARGE — haulage, carriage, a surcharge — rather than
 * goods? By its words (the reference the job logic adds, " · SO-D…", is
 * left out). "AM" / "PM" count only as a line of their own ("AM", "PM
 * delivery"), never inside a product's name ("XC Transition PM Blend").
 */
const CHARGE_RE = /\b(transport|haul|haulage|haulier|carriage|freight|shipping|handling|delivery|fuel|surcharge|pallets?|tail[\s-]?lift|timed|waiting|demurrage|booking|tolls?|congestion|mileage|drop charge|extra drop|collection charge)\b/i;
// A delivery day or slot charged as a line of its own: "Saturday AM",
// "Sat delivery", "Bank holiday", "Next day" (Burcombe's QA03714, 23 Oct 2026:
// a "Saturday AM" line was read as goods, so the bill stopped being a bill of
// jobs). Short lines only — never inside a product's name.
const SLOT_RE = /^((mon|tues|wednes|thurs|fri|satur|sun)day|(mon|tue|wed|thu|fri|sat|sun)\b|weekend|bank holiday|out of hours|next day|same day)\b/i;
export function isChargeLine(text) {
  const w = String(text || '').split(' · ')[0].replace(/\s+/g, ' ').trim();
  if (!w) return false;
  const n = w.split(' ').length;
  if (/^(am|pm)\b/i.test(w) && n <= 3) return true;
  if (SLOT_RE.test(w) && n <= 4) return true;
  return CHARGE_RE.test(w);
}

/**
 * A stored bill line (snake_case or camelCase): is it a haulage / delivery
 * charge? On a PO's Direct Delivery haulage key, an EXTH item (EXTH (Bag),
 * EXTH FS (Bulk) …), a haulage account, or charge words.
 */
export function isChargeBillLine(line) {
  if (!line) return false;
  const key = String(line.po_line_key ?? line.poLineKey ?? '');
  if (key.startsWith('haulage:')) return true;
  const code = String(line.item_code ?? line.itemCode ?? '').trim().toUpperCase();
  if (code.startsWith('EXTH')) return true;
  const acct = String(line.account_code ?? line.accountCode ?? '').trim();
  if (HAULAGE_ACCOUNT_CODES.includes(acct)) return true;
  return isChargeLine(line.description ?? line.product_name ?? '');
}
